import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { ImgixImage } from '@air/component-imgix-image';
import { cn } from '@air/utils-tailwind';

import { SimilarToAssetChip } from '~/components/SearchCommand/SmartSearchImageSearchStatus/SimilarToAssetChip';
import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useClip } from '~/swr-hooks/clips/useClip';

export type ClipSearchProps = {
  clipId: string;
};

const CLIP_SEARCH_THUMBNAIL_PX = 24;

export const ClipSearch = memo(({ clipId }: ClipSearchProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: clip } = useClip(
    { clipId, workspaceId: currentWorkspace?.id || '' },
    { enabled: !!clipId && !!currentWorkspace?.id },
  );
  const [imageLoaded, setImageLoaded] = useState(false);
  const { ui2026 } = useTemporaryFeatureFlags();

  useEffect(() => {
    if (clipId) {
      setImageLoaded(false);
    }
  }, [clipId]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const title = useMemo(() => clip?.title || clip?.importedName || '', [clip?.importedName, clip?.title]);
  const image = useMemo(() => {
    if (!clip?.assets?.image) {
      return null;
    }

    return (
      <ImgixImage
        className={cn(
          'object-cover transition-opacity duration-300 ease-out [image-rendering:smooth]',
          ui2026 ? 'block size-full rounded-4' : 'h-6 w-6 shrink-0 rounded-4',
          imageLoaded ? 'opacity-100' : 'opacity-0',
        )}
        alt={title}
        fallbackUrl={getCloudFrontUrlFromImgixUrl(clip.assets.image)}
        src={clip.assets.image}
        width={CLIP_SEARCH_THUMBNAIL_PX}
        height={CLIP_SEARCH_THUMBNAIL_PX}
        onLoad={handleImageLoad}
        imgixParams={{
          fit: 'crop',
          cs: 'origin',
        }}
      />
    );
  }, [clip?.assets?.image, handleImageLoad, imageLoaded, title, ui2026]);

  if (ui2026) {
    return (
      <SimilarToAssetChip data-testid="CLIP_SEARCH" image={image}>
        {title}
      </SimilarToAssetChip>
    );
  }

  return (
    <div
      className="pointer-events-none flex h-6 min-w-0 max-w-[calc(100%-64px)] items-center gap-1.5 overflow-hidden rounded-4 ads:bg-ads-gray-4 bg-gray-4 pr-1.5 font-medium ads:text-primary text-12 text-gray-12"
      data-testid="CLIP_SEARCH"
    >
      {image}
      <span className="min-w-0 truncate">{title}</span>
    </div>
  );
});

ClipSearch.displayName = 'ClipSearch';
